import { getComponentModelFileCfn } from '@rta/rta-blueprint-library';

export class KitchenDataManager {
	private kitchenData: any = {};
	private kitchenMaterial: any;
	public kitchenDrawPending: boolean = false;
	private currentModelPath: string = '';

	constructor() {
		this.currentModelPath = '';
		this.kitchenDrawPending = false;
	}

	getIslands(): any[] {
		return this.kitchenData.islands || [];
	}

	getDefaultIslandId(): string | undefined {
		return this.kitchenData.defaultIslandId;
	}

	getKitchenMaterial(): any {
		return this.kitchenMaterial;
	}

	getKitchenSize(): { width: number; depth: number; height: number } {
		// Calculate overall kitchen size from islands
		let maxWidth = 0;
		let maxDepth = 0;
		let maxHeight = 0;

		const islands = this.getIslands();
		for (const island of islands) {
			if (island.size) {
				maxWidth = Math.max(maxWidth, island.size.width || 0);
				maxDepth = Math.max(maxDepth, island.size.depth || 0);
				maxHeight = Math.max(maxHeight, island.size.height || 0);
			}
		}

		// Return default dimensions if no islands found
		if (maxWidth === 0 || maxDepth === 0 || maxHeight === 0) {
			return { width: 96, depth: 36, height: 50 };
		}

		return { width: maxWidth, depth: maxDepth, height: maxHeight };
	}

	getCurrentModelPath(): string {
		return this.currentModelPath;
	}

	setCurrentModelPath(modelPath: string): void {
		this.currentModelPath = modelPath;
	}

	getModelPath(surroundKey: string): string | null {
		switch (surroundKey) {
			case 'slatwalnut':
				return 'Slat.glb';
			case 'slatcharcoal':
				return 'Slat.glb';
			case 'slatbright':
				return 'Slat.glb';
			default:
				return null;
		}
	}

	init(kitchen: any): void {
		// console.log('KitchenDataManager.init: Starting with kitchen:', {
			// Check both possible paths - kitchen.islands (legacy) or kitchen.design.islands
			// hasIslands: !!kitchen?.islands,
			// hasDesignIslands: !!kitchen?.design?.islands,
			// islandCount: kitchen?.islands?.children?.length || kitchen?.design?.islands?.children?.length || 0,
			// hasMaterial: !!kitchen?.material
		// });
		
		this.kitchenMaterial = kitchen?.material;

		// Guard against missing data - check both possible paths
		// Legacy structure: kitchen.islands.children
		// Alternative structure: kitchen.design.islands.children
		const islandChildren = kitchen?.islands?.children || kitchen?.design?.islands?.children;
		if (!islandChildren || !Array.isArray(islandChildren) || islandChildren.length === 0) {
			console.warn('KitchenDataManager.init: No islands found in kitchen!', {
				hasKitchen: !!kitchen,
				hasIslands: !!kitchen?.islands,
				hasDesignIslands: !!kitchen?.design?.islands,
				islandsType: kitchen?.islands ? typeof kitchen.islands : 'undefined',
				designIslandsType: kitchen?.design?.islands ? typeof kitchen.design.islands : 'undefined'
			});
			this.kitchenData = { islands: [] };
			return;
		}

		// Get default island ID from the islands container
		const islandsContainer = kitchen?.islands || kitchen?.design?.islands;
		const defaultIslandId = islandsContainer?.defaultIslandId;
		const defaultIsland = islandsContainer?.getDefaultIsland?.();

		// Convert kitchen data structure
		this.kitchenData = {
			defaultIslandId: defaultIslandId || defaultIsland?.id,
			islands: islandChildren.map((island: any, idx: number) => {
				// Check for NaN values in island origin - these indicate calculation errors
				// (usually caused by invalid material keys during kitchen initialization)
				const origin = island.origin ?? { x: 0, y: 0, z: 0 };
				if (isNaN(origin.x) || isNaN(origin.z)) {
					console.warn(`KitchenDataManager: Island "${island.id}" (index ${idx}) has NaN origin:`, {
						originX: origin.x,
						originZ: origin.z,
						isDefault: island.id === (defaultIslandId || defaultIsland?.id)
					});
				}

				return {
					id: island.id,
					isDefault: island.id === (defaultIslandId || defaultIsland?.id),
					connections: island.connections?.children?.map((c: any) => c.id) ??
						(Array.isArray(island.connections) ? island.connections : []),
					axis: island.axis,
					center: island.center,
					origin: origin,
					size: island.islandSize,
					counter: island.getCountertop(),
					panelTrims: island.panelTrims.children.map((trim: any) => {
						return {
							axis: trim.axis,
							center: trim.center,
							origin: trim.origin,
							renderingDetails: trim.renderingDetails
						};
					}),
					panels: island.panels.children.map((panel: any) => {
						try {
							// Get corners for vertices (non-translated)
							const pc = panel.getCornerPoints(false);
							// Get translated center for positioning
							let translatedCenter = panel.center; // Default fallback
							try {
								const po = panel.getCornerPoints(null, { translate: true, rotate: false });
								if (po && po.center) {
									translatedCenter = po.center;
								}
							} catch (e) {
								console.warn('KitchenDataManager: Failed to get translated center, using panel.center:', e);
							}
							return {
								corners: pc, // PanelCorners with corners array
								translatedCenter: translatedCenter, // Use translated center if available
								center: panel.center, // Keep original center as fallback
								size: panel.size,
								thickness: panel.thickness,
								countertopOverhang: panel.countertopOverhang,
								isBackPanel: panel.isBackPanel || false, // Capture if this is a back panel
								originAngle: panel.getOriginAngle(true), // Get angle in radians
								originZ: (() => {
									try {
										// Try getOrigin(true) first - if it exists, it returns radians
										if (typeof panel.getOrigin === 'function') {
											const result = panel.getOrigin(true);
											// console.log('KitchenDataManager: panel.getOrigin(true) =', result);
											return result;
										}
										// Fallback: use axis.z if getOrigin doesn't exist
										if (panel.axis && typeof panel.axis.z === 'number') {
											// console.log('KitchenDataManager: Using panel.axis.z =', panel.axis.z);
											return panel.axis.z;
										}
										console.warn('KitchenDataManager: No getOrigin method or axis.z, defaulting to 0');
										return 0;
									} catch (e) {
										console.error('KitchenDataManager: Error getting origin for Z rotation:', e);
										return 0;
									}
								})(), // Get origin angle in radians for Z rotation
								applianceGroups: panel.panelItemGroups.children.map((panelItemGroup: any) => {
									return {
										id: panelItemGroup.id,
										center: panelItemGroup.center,
										items: panelItemGroup.items.children.map((panelItem: any) => {
											// Placeholder override: when a custom appliance has placeholderModelSku set,
											// render in 3D as if it were a real instance of that catalog SKU. Rather than
											// re-derive the geometry, temporarily swap the panel item's Item for the
											// placeholder and read every field through the real getters (get-item-center,
											// get-cutout-center, get-corners, get-cutout-size, get-panel-sleeve) — so the
											// placeholder sinks into a sibling sleeve, cuts eared cutouts, etc. exactly as a
											// real instance would. 2D is unaffected: it reads panelItem.item.component directly.
											const placeholderModelSku = panelItem.item?.component?.placeholderModelSku ?? null;
											const placeholderComponent = placeholderModelSku
												? kitchen?.context?.components?.findSku?.(placeholderModelSku) ?? null
												: null;
											const hasPlaceholder = !!placeholderComponent;

											// The user's placement choice on the CUSTOM appliance decides whether the placeholder's
											// cutout is applied at all — cutouts get CSG-subtracted from both the surround AND the
											// countertop, so an oversized placeholder cutout on a placement that doesn't naturally
											// penetrate the counter (front-only, bottom-only) will drill a hole in the countertop.
											// 'front and top' always cuts; 'top only' respects the "Cuts Into Countertop" checkbox.
											const customComponent = panelItem.item?.component ?? null;
											const customPlacement = customComponent?.placement;
											const cutsIntoCountertop = customPlacement === 'top only'
												? (customComponent?.distanceBelowCountertop ?? 0) > 0
												: customPlacement === 'front only' || customPlacement === 'bottom only'
													? false
													: true;
											const suppressCut = hasPlaceholder && !cutsIntoCountertop;

											// Identity/instance fields always come from the real (custom) panel item — the
											// placeholder only stands in for geometry and the 3D model, never identity.
											const sku = panelItem.item.sku;
											const id = panelItem.serialize().id;
											const ir = panelItem.instanceRotation;
											const instanceRotation = ir ? { x: ir.x, y: ir.y, z: ir.z } : { x: 0, y: 0, z: 0 };

											const extract = (pi: any) => {
												const modelRotation = pi.item.component?.modelRotation;
												return {
													categoryKey: pi.item.category?.key ?? null,
													placement: (pi.item?.placement || pi.placement) ?? null,
													center: pi.center,
													cutoutCenter: pi.getCenter(true),
													cutoutSize: pi.cutout,
													corners: pi.getCorners(true, true),
													size: pi.size,
													distanceFrontOfFace: pi.item?.distanceFrontOfFace,
													distanceAboveCountertop: pi.item?.distanceAboveCountertop,
													distanceBelowCountertop: pi.item?.distanceBelowCountertop,
													modelOffset: pi.modelOffset || { x: 0, y: 0, z: 0 },
													modelRotation: modelRotation
														? { x: modelRotation.x, y: modelRotation.y, z: modelRotation.z }
														: { x: 0, y: 0, z: 0 },
													isSpacer: pi.item.category?.isSpacer ?? false
												};
											};

											const extracted = hasPlaceholder
												? panelItem.withPlaceholderItem(placeholderModelSku, extract)
												: extract(panelItem);

											return {
												id,
												sku,
												placeholderModelSku,
												modelFile: getComponentModelFileCfn(placeholderComponent ?? panelItem.item?.component),
												...extracted,
												cutoutSize: suppressCut ? { width: 0, height: 0, depth: 0 } : extracted.cutoutSize,
												distanceBelowCountertop: suppressCut ? 0 : extracted.distanceBelowCountertop,
												instanceRotation
											};
										})
									};
								})
							};
						} catch (e) {
							console.error('KitchenDataManager: Error processing panel:', e);
							// Return minimal structure to avoid breaking the loop
							return {
								corners: { corners: [] },
								translatedCenter: panel.center || { x: 0, y: 0, z: 0 },
								center: panel.center || { x: 0, y: 0, z: 0 },
								size: panel.size || { depth: 1 },
								thickness: panel.thickness || 1,
								countertopOverhang: panel.countertopOverhang,
								isBackPanel: panel.isBackPanel || false,
								originAngle: 0,
								originZ: 0, // Default to 0 if getOrigin fails
								applianceGroups: []
							};
						}
					})
				};
			})
		};
	}
}

